import React from 'react';

const Section10Cultural = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-orange-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">📿</span>
      <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
        Cultural Intelligence
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">AI handling sensitive cultural content with respect and accuracy</p>

    {/* Ritual stats */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
      {[
        { icon: '📿', value: '105', label: 'Rituals Embedded' },
        { icon: '🌍', value: '6+', label: 'Cultural Traditions' },
        { icon: '🔍', value: '5', label: 'Matching Factors' },
        { icon: '🎨', value: '2', label: 'Surfaces: Dream Wedding Generator + Moodboard Matcher' },
      ].map((stat, i) => (
        <div key={i} className="bg-gradient-to-br from-orange-50 to-red-50/50 rounded-2xl p-4 text-center border border-orange-200/50 hover:scale-105 transition-transform duration-300">
          <span className="text-2xl block mb-1">{stat.icon}</span>
          <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
          <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
        </div>
      ))}
    </div>

    {/* Personalization flow — guest vs logged-in */}
    <div className="bg-gradient-to-br from-orange-50/50 to-red-50/50 rounded-2xl p-6 border border-orange-200/30 mb-6">
      <h3 className="text-base font-bold text-orange-600 uppercase tracking-wider mb-5 text-center">How Ritual Suggestions Work</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Auto-suggest on generation */}
        <div className="bg-white/80 rounded-xl p-5 border border-orange-200/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-gray-800 text-sm">🎨 Auto-Suggest Pipeline</h4>
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white">Every Theme</span>
          </div>
          <div className="space-y-2 mb-3">
            {[
              'Theme generated (Dream Wedding Generator or Moodboard Matcher) → ritual matching runs automatically',
              'Search query built from theme name + description + style keywords',
              'RAG semantic search finds top 20 candidate rituals from 105 embeddings',
              '5-factor scoring (semantic, style, formality, cultural, symbolism) narrows to top 6',
              'Ritual cards displayed below theme with match % and "Learn More" modal',
            ].map((item, i) => (
              <div key={i} className="flex items-start">
                <span className="text-orange-500 text-xs mr-1.5 mt-0.5">{i + 1}.</span>
                <span className="text-sm text-gray-600">{item}</span>
              </div>
            ))}
          </div>
          <div className="bg-orange-50 rounded-lg px-3 py-2">
            <p className="text-xs font-mono text-orange-700">"Rustic Barn" theme → Handfasting, Unity Candle, Ring Warming</p>
          </div>
        </div>

        {/* What users see */}
        <div className="bg-white/80 rounded-xl p-5 border border-red-200/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-gray-800 text-sm">👁️ What Users See</h4>
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-red-500 to-rose-500 text-white">UI</span>
          </div>
          <div className="space-y-2 mb-3">
            {[
              'Up to 6 ritual cards sorted by match score, displayed below the generated theme',
              'Each card shows: ritual name, culture, description, match percentage',
              '"Learn More" opens a detailed modal with full description and save option',
              '"Explore All Rituals" links to the Cultural Ritual Explainer page',
              'Works in both Single Model and Compare Mode (per-model ritual suggestions)',
            ].map((item, i) => (
              <div key={i} className="flex items-start">
                <span className="text-red-500 text-xs mr-1.5 mt-0.5">{i + 1}.</span>
                <span className="text-sm text-gray-600">{item}</span>
              </div>
            ))}
          </div>
          <div className="bg-red-50 rounded-lg px-3 py-2">
            <p className="text-xs font-mono text-red-700">Compare Mode: each model tab shows its own ritual suggestions</p>
          </div>
        </div>
      </div>
    </div>

    {/* Ritual data model */}
    <div className="bg-gradient-to-br from-orange-50/50 to-red-50/50 rounded-2xl p-6 border border-orange-200/30 mb-6">
      <h3 className="text-base font-bold text-orange-600 uppercase tracking-wider mb-5 text-center">Ritual Data Model</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { field: 'Name', example: 'Saat Phere (Seven Circles)', icon: '📛' },
          { field: 'Culture', example: 'Indian Wedding Traditions', icon: '🌍' },
          { field: 'Description', example: 'Full ceremony description...', icon: '📝' },
          { field: 'Duration', example: '15-20 minutes', icon: '⏱️' },
          { field: 'Complexity', example: 'simple / medium / complex', icon: '📊' },
          { field: 'Formality', example: 'casual → very-formal', icon: '👔' },
          { field: 'Themes', example: 'romantic, traditional, elegant', icon: '🎨' },
          { field: 'Symbolism', example: 'eternity, unity, commitment', icon: '💫' },
        ].map((f, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-3 border border-orange-100/50 text-center">
            <span className="text-lg block mb-1">{f.icon}</span>
            <p className="text-sm font-bold font-mono text-orange-700">{f.field}</p>
            <p className="text-xs text-gray-400 mt-1">{f.example}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Engineering details */}
    <div className="bg-gradient-to-r from-orange-50/80 to-red-50/80 rounded-2xl p-5 border border-orange-200/30">
      <h3 className="text-base font-bold text-orange-600 uppercase tracking-wider mb-3">🎯 Cultural Engineering Decisions</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { q: 'Why RAG for rituals (not keyword search)?', a: '"Symbol of eternal love" finds rings, handfasting, and mangalsutra across cultures. Keyword search would miss these semantic connections entirely.' },
          { q: 'Semantic matching without stereotyping', a: 'The system matches by theme aesthetics and symbolism, not by user ethnicity. A "romantic garden" theme gets handfasting suggestions regardless of the user\'s background.' },
          { q: 'Cross-cultural discovery', a: 'RAG naturally surfaces similar rituals across cultures: Saat Phere (Indian circles) relates to Circling (Jewish tradition). Users discover traditions they didn\'t know existed.' },
          { q: 'Culture filter from natural language', a: 'When users ask VowBot "Indian rituals", the system extracts "Indian" from the query and filters results to that culture while still using theme context for ranking. No rigid dropdowns, just natural language.' },
        ].map((item, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-orange-100/50">
            <p className="text-sm font-bold text-orange-700 mb-1">{item.q}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>

);

export default Section10Cultural;
